import { CouponInput } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { PauseInputDto } from "@modules/common/pauseObject/gateway/pauseApiDto"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { ProgramBundleSubscriberApiGateway } from "@modules/programBundle/programBundleSubscribers/gateway/programBundleSubscriberApiGateway"
import {
  ProgramBundleSubscriberDto,
  ProgramBundleSubscriberDtoSchema,
} from "@modules/programBundle/programBundleSubscribers/gateway/programBundleSubscriberDto"
import { Paginated, toPage } from "@modules/utils/paginated"

export class ProgramBundleSubscriberApi implements ProgramBundleSubscriberApiGateway {
  private baseUrl = "/program-bundles"

  constructor(private api: ApiClient) {}

  async getProgramBundleSubscriber(programBundleId: string, subscriberId: string) {
    const result = await this.api.get<ProgramBundleSubscriberDto>(
      `${this.baseUrl}/${programBundleId}/subscribers/${subscriberId}`,
    )
    return ProgramBundleSubscriberDtoSchema.parse(result.data)
  }

  async getProgramBundleSubscribers(programBundleId: string, page: number, size: number) {
    const result = await this.api.get<Paginated<ProgramBundleSubscriberDto>>(
      `${this.baseUrl}/${programBundleId}/subscribers`,
      {
        params: {
          page,
          size,
        },
      },
    )
    const pageResult = toPage(result.data)

    return {
      ...pageResult,
      content: pageResult.content.map((subscriber) => ProgramBundleSubscriberDtoSchema.parse(subscriber)),
    }
  }

  async searchProgramBundleSubscribers(programBundleId: string, page = 0, size = 40, searchTerm?: string) {
    const result = await this.api.get<Paginated<ProgramBundleSubscriberDto>>(
      `${this.baseUrl}/${programBundleId}/subscribers/search`,
      {
        params: {
          nameOrUsername: searchTerm,
          page,
          size,
        },
      },
    )
    const pageResult = toPage(result.data)

    return {
      ...pageResult,
      content: pageResult.content.map((subscriber) => ProgramBundleSubscriberDtoSchema.parse(subscriber)),
    }
  }

  async getProgramBundleSubscription(programBundleId: string) {
    const result = await this.api.get<ProgramBundleSubscriberDto>(`${this.baseUrl}/${programBundleId}/subscription`)

    return result.data ? ProgramBundleSubscriberDtoSchema.parse(result.data) : null
  }

  async subscribeProgramBundle(programBundleId: string) {
    const result = await this.api.post<ProgramBundleSubscriberDto>(`${this.baseUrl}/${programBundleId}/subscribe`)
    return ProgramBundleSubscriberDtoSchema.parse(result.data)
  }

  async unsubscribeProgramBundle(programBundleId: string) {
    await this.api.post<{ status: string }>(`${this.baseUrl}/${programBundleId}/unsubscribe`)
  }

  async updateSubscriberPause(programBundleId: string, subscriberId: string, pause: PauseInputDto) {
    await this.api.post(`${this.baseUrl}/${programBundleId}/subscribers/${subscriberId}/pause`, pause)
  }

  async resumeSubscription(programBundleId: string, subscriberId: string) {
    await this.api.post(`${this.baseUrl}/${programBundleId}/subscribers/${subscriberId}/resume`)
  }

  async updateSubscriberInternalNote(programBundleId: string, subscriberId: string, note: string) {
    const result = await this.api.post(`${this.baseUrl}/${programBundleId}/subscribers/${subscriberId}/note`, {
      note,
    })

    return ProgramBundleSubscriberDtoSchema.parse(result.data)
  }

  async removeSubscriber(programBundleId: string, subscriberId: string, cancelAtPeriodEnd: boolean) {
    await this.api.delete(`${this.baseUrl}/${programBundleId}/subscribers/${subscriberId}`, {
      data: {
        cancelAtPeriodEnd,
      },
    })
  }

  async removeSubscriptionDiscount(programBundleId: string, subscriberId: string) {
    await this.api.delete(`${this.baseUrl}/${programBundleId}/subscribers/${subscriberId}/coupons`)
  }

  async applySubscriptionDiscount(programBundleId: string, subscriberId: string, coupon: CouponInput) {
    await this.api.post(`${this.baseUrl}/${programBundleId}/subscribers/${subscriberId}/coupons`, coupon)
  }

  async unfollowProgramBundle(programBundleId: string, programIds: string[]) {
    await this.api.post(`${this.baseUrl}/${programBundleId}/unfollow`, {
      programIds,
    })
  }

  async followProgramBundle(programBundleId: string, programIds: string[]) {
    await this.api.post(`${this.baseUrl}/${programBundleId}/follow`, {
      programIds,
    })
  }
}
